import React, { useEffect, useMemo, useState } from "react";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addWeeks,
  addMonths,
  subWeeks,
  subMonths,
  eachDayOfInterval,
  isSameDay,
  subDays,
  addDays,
} from "date-fns";
import "./Shifts.scss";
import axios from "axios";
import { BASE_API_URL } from "../../utils/consts";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as ArrowIcon } from "../../assets/images/icons/arrowLeft.svg";
import { useTranslation } from "react-i18next";
import { ReactComponent as AddIcon } from "../../assets/images/icons/plusCircle.svg";
import { ReactComponent as ShiftEmployeeIcon } from "../../assets/images/icons/shiftEmployee.svg";
import AddShiftModal from "../../Components/Modals/AddShiftModal/AddShiftModal";
import VoiceShiftButton from "../../Components/VoiceShift/VoiceShiftButton";
import { ShiftAbbreviation, ShiftType, ShiftTypePanorama } from "./utils";
import { Button, Input, Popover, Select, Collapse, message } from "antd";
import { setLoader } from "../../utils/redux/slices/loaderSlice";
import {
  ClockCircleOutlined,
  IssuesCloseOutlined,
  RedoOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { usePermissions } from "../../utils/usePermissions";
import { isSuperAdmin } from "../../utils/permissions";
import { isLocationActive } from "../../utils/helpers/locationVisibility";
const { Option } = Select;
interface Shift {
  _id: string;
  userId: string;
  clientId: string;
  date: string;
  startTime: string;
  endTime: string;
  shiftType: string;
  durationInHours: number;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  clientId: string;
  avatar: {
    url: string;
  };
  shifts: Shift[];
}

interface ShiftSchedulerProps {
  //   users: User[];
}

const ShiftScheduler: React.FC<ShiftSchedulerProps> = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<"week" | "month">("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const userData = useSelector((state: any) => state.userData.user);
  const [users, setUsers] = useState<any>(undefined);
  const [enteredUsers, setEnteredUsers] = useState("");
  const [selectedShiftLocation, setSelectedShiftLocation] =
    useState<any>(undefined);
  const [locations, setLocations] = useState<any>(undefined);
  const [workSectors, setWorkSectors] = useState<any>(undefined);
  const [shiftModalOpen, setShiftModalOpen] = useState<any>(undefined);
  const [selectedSector, setSelectedSector] = useState<string | undefined>(
    undefined,
  );
  const [isCompactWeekView, setIsCompactWeekView] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [openUserKey, setOpenUserKey] = useState<string | undefined>(undefined);
  const [refreshTick, setRefreshTick] = useState(0);

  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const canViewShifts =
    isSuperAdmin(userData?.roles) || hasPermission("shifts:view");
  const canCreateSingle =
    isSuperAdmin(userData?.roles) || hasPermission("shifts:create-single");
  const canCreateMultiple =
    isSuperAdmin(userData?.roles) || hasPermission("shifts:create-multiple");
  const canCreateLeave =
    isSuperAdmin(userData?.roles) || hasPermission("shifts:create-leave-shift");
  const canDeleteSingle =
    isSuperAdmin(userData?.roles) || hasPermission("shifts:delete-single");
  const canDeleteMultiple =
    isSuperAdmin(userData?.roles) || hasPermission("shifts:delete-multiple");
  const canCreateAny = canCreateSingle || canCreateMultiple || canCreateLeave;
  const canUseVoiceShift =
    isSuperAdmin(userData?.roles) || hasPermission("shifts:voice-shift");
  const getFormattedDate = (date: Date) => {
    return format(date, "yyyy-MM-dd"); // Local time, 'YYYY-MM-DD'
  };

  const filterActiveUsers = (data: any[] = []) =>
    data.filter((item) => !item?.user?.isFrozen);

  // Calculate dynamic date range based on currentDate and view
  const fromDate = getFormattedDate(
    view === "week"
      ? startOfWeek(currentDate, { weekStartsOn: 1 }) // Week starts on Monday
      : startOfMonth(currentDate),
  );

  const baseDate =
    view === "week"
      ? startOfWeek(currentDate, { weekStartsOn: 1 }) // Ponedeljak je prvi dan u nedelji
      : startOfMonth(currentDate);

  const fromDateISO = baseDate.toISOString();

  const toDate = getFormattedDate(
    view === "week"
      ? endOfWeek(currentDate, { weekStartsOn: 1 })
      : endOfMonth(currentDate),
  );

  const baseToDate =
    view === "week"
      ? endOfWeek(currentDate, { weekStartsOn: 1 })
      : endOfMonth(currentDate);

  const toDateISO = baseToDate.toISOString();
  useEffect(() => {
    if (userData.clientId) {
      dispatch(setLoader(true));
      const adjustedFrom = format(subDays(new Date(fromDate), 1), "yyyy-MM-dd");
      const adjustedTo = format(addDays(new Date(toDate), 1), "yyyy-MM-dd");
      axios
        .get(
          `${BASE_API_URL}/users-with-shifts/${userData.clientId}?from=${adjustedFrom}&to=${adjustedTo}`,
        )
        .then((res) => {
          setUsers(filterActiveUsers(res.data.data));
          // You can handle the users and shifts here if needed
        })
        .catch((err) => console.error(err))
        .finally(() => dispatch(setLoader(false)));
    }
  }, [userData.clientId, currentDate, view, refreshTick]);

  useEffect(() => {
    if (userData?.clientId) {
      // ${userData?.clientId}
      axios

        .get(`${BASE_API_URL}/${userData?.clientId}/locations`)
        .then((res) => {
          setLocations((res.data || []).filter(isLocationActive));
        })
        .catch((err) => console.error(err));
      axios

        .get(`${BASE_API_URL}/admin/${userData?.clientId}/getWorkSectors`)
        .then((res) => {
          setWorkSectors(res.data);
        })
        .catch((err) => console.error(err));
    }
  }, [userData]);
  useEffect(() => {
    const handleResize = () => {
      setIsCompactWeekView(window.innerWidth < 1024);
      setIsMobileView(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePrev = () => {
    setCurrentDate(
      view === "week"
        ? subWeeks(currentDate, 1) // sedmica unazad, pa još -1 dan
        : subMonths(currentDate, 1), // mesec unazad, pa još -1 dan
    );
  };

  const handleNext = () => {
    setCurrentDate(
      view === "week"
        ? addWeeks(currentDate, 1) // sedmica unapred, pa još +1 dan
        : addMonths(currentDate, 1), // mesec unapred, pa još +1 dan
    );
  };

  const getVisibleDates = () => {
    if (view === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      return eachDayOfInterval({ start, end });
    }
  };

  function formatTimeToHHMM(dateString: any) {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  //   function getTotalHours(shifts: any) {
  //     return shifts.reduce(
  //       (total: any, shift: any) => total + shift.durationInHours,
  //       0
  //     );
  //   }

  function getTotalHours(shifts: Shift[]): number {
    let filteredShifts: Shift[] = [];

    if (view === "week") {
      // Računamo početak i kraj nedelje (ponedeljak - nedelja)
      const dayOfWeek = currentDate.getDay(); // 0 = nedelja
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(currentDate);
      monday.setDate(currentDate.getDate() + diffToMonday);
      monday.setHours(0, 0, 0, 0);

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      filteredShifts = shifts.filter((shift) => {
        const shiftDate = new Date(shift.date);
        return shiftDate >= monday && shiftDate <= sunday;
      });
    } else if (view === "month") {
      const month = currentDate.getMonth();
      const year = currentDate.getFullYear();

      filteredShifts = shifts.filter((shift) => {
        const shiftDate = new Date(shift.date);
        return (
          shiftDate.getMonth() === month && shiftDate.getFullYear() === year
        );
      });
    }

    // Sabiranje sati
    return filteredShifts.reduce(
      (total, shift) => total + shift.durationInHours,
      0,
    );
  }

  const addShift = (value: any) => {
    if (value.shiftType === ShiftTypePanorama.LEAVES && !canCreateLeave) {
      message.warning(t("No permission to create leave shift"));
      return;
    }
    if (value.isMultipleShift && !canCreateMultiple) {
      message.warning(t("No permission to create multiple shifts"));
      return;
    }
    if (
      !value.isMultipleShift &&
      value.shiftType !== ShiftTypePanorama.LEAVES &&
      !canCreateSingle
    ) {
      message.warning(t("No permission to create shift"));
      return;
    }
    // ako je odsustvo (LEAVES)
    if (value.shiftType === ShiftTypePanorama.LEAVES) {
      console.log("value", value);
      const payload: any = {
        userId: shiftModalOpen.user._id,
        clientId: userData.clientId,
        startDate: value.date,
        shiftStartTime: "00:00",
        shiftEndTime: "23:59",
        shiftType: value.shiftType,
        timeFrame: value.timeFrame, // leaveType
        excludeDays: [],
        customExcludeDates: value.excludedDates,
        leavePayPercentage: value.leavePayPercentage,
        leavePayType: value.leavePayType,
      };

      // ⚡️ Ako je odsustvo po satu, pošalji i broj sati
      if (value.leavePayType === ShiftTypePanorama.HOURS) {
        payload.durationInHours = Number(value.durationInHours) || 0;
      }

      // ⚡️ Dodaj locationId ako postoji
      if (value.locationId) {
        payload.locationId = value.locationId;
      }

      axios
        .post(`${BASE_API_URL}/create-multiple-shifts`, payload)
        .then((res) => {
          setShiftModalOpen(undefined);
          if (userData.clientId) {
            dispatch(setLoader(true));
            const adjustedFrom = format(
              subDays(new Date(fromDate), 1),
              "yyyy-MM-dd",
            );
            const adjustedTo = format(
              addDays(new Date(toDate), 1),
              "yyyy-MM-dd",
            );
            axios
              .get(
                `${BASE_API_URL}/users-with-shifts/${userData.clientId}?from=${adjustedFrom}&to=${adjustedTo}`,
              )
              .then((res) => setUsers(filterActiveUsers(res.data.data)))
              .catch((err) => console.error(err))
              .finally(() => dispatch(setLoader(false)));
          }
        })
        .catch((err) => console.error(err));

      return;
    }

    // ako je višestruka smena (ali nije LEAVES)
    if (value.isMultipleShift) {
      const multipleShiftsPayload: any = {
        userId: shiftModalOpen.user._id,
        clientId: userData.clientId,
        startDate: value.date,
        shiftStartTime: formatTimeToHHMM(value.startTime),
        shiftEndTime: formatTimeToHHMM(value.endTime),
        shiftType: value.shiftType,
        timeFrame: value.timeFrame,
        excludeDays: value.excludedWeekDays,
        customExcludeDates: value.excludedDates,
      };

      // ⚡️ Dodaj locationId ako postoji
      if (value.locationId) {
        multipleShiftsPayload.locationId = value.locationId;
      }

      axios
        .post(`${BASE_API_URL}/create-multiple-shifts`, multipleShiftsPayload)
        .then((res) => {
          setShiftModalOpen(undefined);
          if (userData.clientId) {
            dispatch(setLoader(true));
            const adjustedFrom = format(
              subDays(new Date(fromDate), 1),
              "yyyy-MM-dd",
            );
            const adjustedTo = format(
              addDays(new Date(toDate), 1),
              "yyyy-MM-dd",
            );
            axios
              .get(
                `${BASE_API_URL}/users-with-shifts/${userData.clientId}?from=${adjustedFrom}&to=${adjustedTo}`,
              )
              .then((res) => setUsers(filterActiveUsers(res.data.data)))
              .catch((err) => console.error(err))
              .finally(() => dispatch(setLoader(false)));
          }
        })
        .catch((err) => console.error(err));
    } else {
      // ako je pojedinačna smena
      const singleShiftPayload: any = {
        userId: shiftModalOpen.user._id,
        clientId: userData.clientId,
        date: value.date,
        startTime: value.startTime,
        endTime: value.endTime,
        shiftType: value.shiftType,
      };

      // ⚡️ Dodaj locationId ako postoji
      if (value.locationId) {
        singleShiftPayload.locationId = value.locationId;
      }

      axios
        .post(`${BASE_API_URL}/shifts/create`, singleShiftPayload)
        .then((res) => {
          setShiftModalOpen(undefined);
          if (userData.clientId) {
            dispatch(setLoader(true));
            const adjustedFrom = format(
              subDays(new Date(fromDate), 1),
              "yyyy-MM-dd",
            );
            const adjustedTo = format(
              addDays(new Date(toDate), 1),
              "yyyy-MM-dd",
            );
            axios
              .get(
                `${BASE_API_URL}/users-with-shifts/${userData.clientId}?from=${adjustedFrom}&to=${adjustedTo}`,
              )
              .then((res) => setUsers(filterActiveUsers(res.data.data)))
              .catch((err) => console.error(err))
              .finally(() => dispatch(setLoader(false)));
          }
        })
        .catch((err) => console.error(err));
    }
  };

  const visibleDates = getVisibleDates();

  function formatTime(time: any) {
    // Ako je ISO format, parsiraj i vrati HH:mm
    const isoDate = Date.parse(time);
    if (!isNaN(isoDate)) {
      const date = new Date(time);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    // Inače vrati direktno (pretpostavlja se da je u HH:mm formatu)
    return time;
  }

  function colorToRgba(color: string, alpha: number = 0.1): string {
    const dummy = document.createElement("div");
    dummy.style.color = color;
    document.body.appendChild(dummy);

    const computedColor = getComputedStyle(dummy).color;
    document.body.removeChild(dummy);

    const rgbMatch = computedColor.match(/^rgb\((\d+), (\d+), (\d+)\)$/);
    if (rgbMatch) {
      const [_, r, g, b] = rgbMatch;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // fallback
    return `rgba(0, 0, 0, ${alpha})`;
  }

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "";
    return ((firstName?.[0] ?? "") + (lastName?.[0] ?? "")).toUpperCase();
  };

  function formatDuration(duration: number): string | number {
    const decimalPart = duration.toString().split(".")[1];
    if (decimalPart && decimalPart.length >= 2) {
      return duration.toFixed(1);
    }
    return duration;
  }

  function getShiftLocationName(shift: any): string {
    if (!shift.locationId) return "";
    if (typeof shift.locationId === "object") return shift.locationId?.name ?? "";
    const found = locations?.find((l: any) => l._id === shift.locationId);
    return found?.name ?? "";
  }

  const deleteShift = (shift: any) => {
    if (!canDeleteSingle) {
      message.warning(t("No permission to delete shift"));
      return;
    }
    axios
      .delete(`${BASE_API_URL}/shifts/delete?shiftId=${shift._id}`)
      .then((res) => {
        if (userData.clientId) {
          dispatch(setLoader(true));
          const adjustedFrom = format(
            subDays(new Date(fromDate), 1),
            "yyyy-MM-dd",
          );
          const adjustedTo = format(addDays(new Date(toDate), 1), "yyyy-MM-dd");
          axios
            .get(
              `${BASE_API_URL}/users-with-shifts/${userData.clientId}?from=${adjustedFrom}&to=${adjustedTo}`,
            )
            .then((res) => {
              setUsers(filterActiveUsers(res.data.data));
              // You can handle the users and shifts here if needed
            })
            .catch((err) => console.error(err))
            .finally(() => dispatch(setLoader(false)));
        }
      })
      .catch(() => console.log("error"));
  };

  const deleteMultipleShifts = (shift: any) => {
    if (!canDeleteMultiple) {
      message.warning(t("No permission to delete multiple shifts"));
      return;
    }
    const startDate = startOfMonth(shift.date);
    const endDate = endOfMonth(shift.date);
    axios
      .delete(
        `${BASE_API_URL}/shifts/delete?unitId=${shift?.unitId}&dateFrom=${startDate}&dateTo=${endDate}`,
      )
      .then((res) => {
        if (userData.clientId) {
          dispatch(setLoader(true));
          const adjustedFrom = format(
            subDays(new Date(fromDate), 1),
            "yyyy-MM-dd",
          );
          const adjustedTo = format(addDays(new Date(toDate), 1), "yyyy-MM-dd");
          axios
            .get(
              `${BASE_API_URL}/users-with-shifts/${userData.clientId}?from=${adjustedFrom}&to=${adjustedTo}`,
            )
            .then((res) => {
              setUsers(filterActiveUsers(res.data.data));
              // You can handle the users and shifts here if needed
            })
            .catch((err) => console.error(err))
            .finally(() => dispatch(setLoader(false)));
        }
      })
      .catch(() => console.log("error"));
  };

  const filteredUsers = useMemo(() => {
    if (!users) return [];

    const searchLower = enteredUsers.toLowerCase();

    return users.filter((item: any) => {
      const firstName = item.user?.firstName?.toLowerCase() || "";
      const lastName = item.user?.lastName?.toLowerCase() || "";
      const locationName = item.user?.locationName?.toLowerCase() || "";

      const matchesName =
        searchLower &&
        (firstName.includes(searchLower) || lastName.includes(searchLower));

      const matchesLocation =
        selectedShiftLocation &&
        locationName.includes(selectedShiftLocation.toLowerCase());

      // 🔹 Novi deo — provera sektora
      const userSectors =
        item.user?.positionInSector?.map((pos: any) =>
          pos?.workSector?.name?.toLowerCase(),
        ) || [];
      const matchesSector =
        selectedSector && userSectors.includes(selectedSector.toLowerCase());

      // Kombinacije filtera
      if (searchLower && selectedShiftLocation && selectedSector) {
        return matchesName && matchesLocation && matchesSector;
      } else if (searchLower && selectedShiftLocation) {
        return matchesName && matchesLocation;
      } else if (searchLower && selectedSector) {
        return matchesName && matchesSector;
      } else if (selectedShiftLocation && selectedSector) {
        return matchesLocation && matchesSector;
      } else if (searchLower) {
        return matchesName;
      } else if (selectedShiftLocation) {
        return matchesLocation;
      } else if (selectedSector) {
        return matchesSector;
      }

      return true;
    });
  }, [enteredUsers, selectedShiftLocation, selectedSector, users]);

  const onChangeShiftLocation = (value: string) => {
    setSelectedShiftLocation(value);
  };

  if (!canViewShifts) {
    return (
      <div className="shift-scheduler">{t("No permission to view shifts")}</div>
    );
  }

  return (
    <div className="shift-scheduler">
      {/* Header */}
      <div className="scheduler-header">
        <div className="flex">
          <div className="period">
            <label>Period</label>
            <div className="selectDate">
              <button onClick={handlePrev}>
                <ArrowIcon />
              </button>
              <h2>
                {view === "week"
                  ? `${format(
                      startOfWeek(currentDate, { weekStartsOn: 1 }),
                      "MMM d",
                    )} - ${format(
                      endOfWeek(currentDate, { weekStartsOn: 1 }),
                      "MMM d, yyyy",
                    )}`
                  : `${format(currentDate, "MMMM yyyy")}`}
              </h2>
              <button onClick={handleNext} className="nextBtn">
                <ArrowIcon />
              </button>
            </div>
          </div>
          <div className="filterUsers">
            <label htmlFor="usersInput" className="dropdownLabel">
              {t("Users")}
            </label>
            <Input
              id="usersInput"
              style={{ minWidth: 200, width: "100%" }}
              placeholder={t("Search user")}
              value={enteredUsers}
              onChange={(e) => setEnteredUsers(e.target.value)}
            />
          </div>
          <div className="filterByLocation">
            <label htmlFor="leaveTypeDropdown" className="dropdownLabel">
              {t("Locations")}
            </label>
            <Select
              id="leaveTypeDropdown"
              style={{ minWidth: 200, width: "100%" }}
              placeholder={t("Select a location")}
              optionFilterProp="label"
              allowClear={true}
              onClear={() => {
                setSelectedShiftLocation(undefined);
              }}
              onChange={onChangeShiftLocation}
              value={selectedShiftLocation}
            >
              {" "}
              {locations?.map((item: any, index: number) => {
                return (
                  <Option value={item.name} key={index}>
                    {item.name}
                  </Option>
                );
              })}
            </Select>
          </div>
          <div className="filterBySector">
            <label htmlFor="sectorDropdown" className="dropdownLabel">
              {t("Work Sector")}
            </label>
            <Select
              id="sectorDropdown"
              style={{ minWidth: 200, width: "100%" }}
              placeholder={t("Select a sector")}
              optionFilterProp="label"
              allowClear={true}
              onClear={() => setSelectedSector(undefined)}
              onChange={(value) => setSelectedSector(value)}
              value={selectedSector}
            >
              {workSectors?.map((item: any, index: number) => (
                <Option value={item.name} key={index}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="viewType">
          <label>{t("View Type")}</label>
          <div className="viewType-row">
            <div className="viewType-toggle">
              <button
                onClick={() => setView("week")}
                className={`${view === "week" ? "active" : ""}`}
              >
                {t("Week")}
              </button>
              <button
                onClick={() => setView("month")}
                className={`${view === "month" ? "active" : ""}`}
              >
                {t("Month")}
              </button>
            </div>
            {canUseVoiceShift && (
              <VoiceShiftButton
                clientId={userData.clientId}
                onShiftCreated={() => setRefreshTick((t) => t + 1)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Desktop grid */}
      {!isMobileView && (
        <div className="scheduler-grid">
          <div className="grid-row grid-header">
            <div className="grid-cell employee-header">
              <ShiftEmployeeIcon />
            </div>
            <div className="dates-header">
              {visibleDates.map((date) => (
                <div key={date.toISOString()} className="grid-cell date-header">
                  <div>{t(format(date, "EEEEEE"))}</div>
                  <div>{format(date, "d")}</div>
                </div>
              ))}
            </div>
          </div>

          {filteredUsers?.map((user: any) => {
            return (
              <div key={user._id} className="grid-row">
                <div className="grid-cell employee-name">
                  <div className="userInformation">
                    {[user.user.firstName, user.user.lastName]
                      .filter(Boolean)
                      .map((s) => s.trim())
                      .join(" ")}
                    <div
                      data-fulltext={user?.user?.position}
                      style={{
                        color:
                          user?.user?.positionInSector[0]?.workSector?.color ??
                          user.user.color ??
                          "rgb(0 0 0 / 100%)",
                        marginTop: 2,
                        background: user?.user?.positionInSector[0]?.workSector
                          ?.color
                          ? colorToRgba(
                              user?.user?.positionInSector[0]?.workSector
                                ?.color,
                            )
                          : (colorToRgba(user.user.color, 0.1) ??
                            "rgb(0 0 0 / 10%)"),
                      }}
                    >
                      <span>
                        {user?.user?.positionInSector[0]
                          ? user?.user?.positionInSector[0]?.name
                          : "-"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="dates-row">
                  {visibleDates.map((dateStr, index) => {
                    const date = new Date(dateStr);
                    const shiftsForDate = user.shifts.filter((shift: any) => {
                      const shiftDate = new Date(shift.date);
                      return isSameDay(date, shiftDate);
                    });

                    return (
                      <div key={index} className="grid-cell shift-data">
                        {shiftsForDate.length > 0
                          ? shiftsForDate.map((shift: any, i: number) => {
                              const shiftSingle: { shiftType: ShiftType } = {
                                shiftType: shift.shiftType,
                              };

                              return (
                                <Popover
                                  content={
                                    <div className="deleteShifts">
                                      {getShiftLocationName(shift) && (
                                        <div style={{ marginBottom: 8, fontSize: 12, color: "#555", borderBottom: "1px solid #f0f0f0", paddingBottom: 6 }}>
                                          {getShiftLocationName(shift)}
                                        </div>
                                      )}
                                      {canDeleteSingle && (
                                        <Button
                                          onClick={() => deleteShift(shift)}
                                        >
                                          {t("Delete shift")}
                                        </Button>
                                      )}
                                      {canDeleteMultiple && shift?.unitId && (
                                        <>
                                          <br />
                                          <Button
                                            onClick={() =>
                                              deleteMultipleShifts(shift)
                                            }
                                          >
                                            {t("Delete multiple shifts")}
                                          </Button>
                                        </>
                                      )}
                                      {!canDeleteSingle &&
                                        !(
                                          canDeleteMultiple && shift?.unitId
                                        ) && <div>{t("No permission")}</div>}
                                    </div>
                                  }
                                  trigger="click"
                                >
                                  <div
                                    className={`shift ${view}`}
                                    key={i}
                                    style={{
                                      border: `2px solid ${
                                        user?.user?.color ?? "grey"
                                      }`,
                                      margin: 4,
                                      backgroundColor: user?.user?.color
                                        ? `rgba(${parseInt(
                                            user?.user?.color.slice(1, 3),
                                            16,
                                          )}, ${parseInt(
                                            user?.user?.color.slice(3, 5),
                                            16,
                                          )}, ${parseInt(
                                            user?.user?.color.slice(5, 7),
                                            16,
                                          )}, 0.5)`
                                        : "rgba(128, 128, 128, 0.1)",
                                    }}
                                  >
                                    <div className="shiftBody">
                                      {view === "week" ? (
                                        shift.shiftType === "HOURS" ? (
                                          <div className="shiftDate">
                                            <div>
                                              <span>
                                                {formatTime(shift.startTime)}
                                              </span>{" "}
                                              <i>-</i>{" "}
                                              <span>
                                                {formatTime(shift.endTime)}
                                              </span>
                                            </div>
                                            <span>
                                              {formatDuration(
                                                shift.durationInHours,
                                              )}
                                              h
                                            </span>
                                          </div>
                                        ) : shift.shiftType ===
                                          ShiftTypePanorama.DAILY_WAGE ? (
                                          isCompactWeekView ? (
                                            "D"
                                          ) : (
                                            <div>
                                              <div>DNEVNICA</div>
                                              {getShiftLocationName(shift) && (
                                                <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>{getShiftLocationName(shift)}</div>
                                              )}
                                            </div>
                                          )
                                        ) : shift.shiftType ===
                                          ShiftTypePanorama.TEMPORARY_HELP ? (
                                          isCompactWeekView ? (
                                            "I"
                                          ) : (
                                            <div>
                                              <div>ISPOMOC</div>
                                              {getShiftLocationName(shift) && (
                                                <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>{getShiftLocationName(shift)}</div>
                                              )}
                                            </div>
                                          )
                                        ) : (
                                          isCompactWeekView ? "OD" : (
                                            <div>
                                              <div>ODSUSTVO</div>
                                              {getShiftLocationName(shift) && (
                                                <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>{getShiftLocationName(shift)}</div>
                                              )}
                                            </div>
                                          )
                                        )
                                      ) : shift.shiftType === "HOURS" ? (
                                        <div className="shiftDate">
                                          {formatTime(shift.startTime)}
                                          <br />
                                          {formatTime(shift.endTime)}
                                        </div>
                                      ) : (
                                        <>
                                          {shift.shiftType ===
                                          ShiftTypePanorama.DAILY_WAGE ? (
                                            <div className="fullTime">
                                              <span>D</span>
                                            </div>
                                          ) : shift.shiftType === "LEAVES" ? (
                                            <div className="fullTime">
                                              <span>OD</span>
                                            </div>
                                          ) : (
                                            <div className="partTime">
                                              <span>I</span>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      <Popover
                                        content={
                                          <div
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div>{shift.shiftType}</div>
                                            {getShiftLocationName(shift) && (
                                              <div style={{ marginTop: 4, color: "#555" }}>{getShiftLocationName(shift)}</div>
                                            )}
                                          </div>
                                        }
                                        title={
                                          <div
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {t("Shift Info")}
                                          </div>
                                        }
                                        placement="top"
                                      >
                                        <div
                                          className="shiftType"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {
                                            ShiftAbbreviation[
                                              shiftSingle.shiftType
                                            ]
                                          }
                                        </div>
                                      </Popover>
                                    </div>
                                  </div>
                                </Popover>
                              );
                            })
                          : "-"}
                        <div
                          className={`addShift ${
                            !canCreateAny ? "disabled" : ""
                          }`}
                          onClick={() => {
                            if (!canCreateAny) {
                              message.warning(
                                t("No permission to create shift"),
                              );
                              return;
                            }
                            setShiftModalOpen({
                              user: user.user,
                              date: dateStr,
                              shift: shiftsForDate,
                            });
                          }}
                        >
                          <AddIcon />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile / tablet cards */}
      {isMobileView && (
        <Collapse
          accordion
          bordered={false}
          className="mobile-shifts"
          activeKey={openUserKey}
          onChange={(key) =>
            setOpenUserKey(typeof key === "string" ? key : undefined)
          }
        >
          {filteredUsers?.map((user: any) => {
            const rawTotal = getTotalHours(user.shifts);
            const userTotal =
              rawTotal == null ? 0 : Math.round(Number(rawTotal) * 100) / 100;
            return (
              <Collapse.Panel
                key={user._id}
                header={
                  <div className="mobile-user-header">
                    <div className="mobile-user-info">
                      <div className="mobile-user-name">
                        {[user.user.firstName, user.user.lastName]
                          .filter(Boolean)
                          .map((s) => s.trim())
                          .join(" ")}
                      </div>
                      <div
                        className="mobile-user-position"
                        style={{
                          color:
                            user?.user?.positionInSector[0]?.workSector
                              ?.color ??
                            user.user.color ??
                            "rgb(0 0 0 / 100%)",
                          background: user?.user?.positionInSector[0]
                            ?.workSector?.color
                            ? colorToRgba(
                                user?.user?.positionInSector[0]?.workSector
                                  ?.color,
                              )
                            : (colorToRgba(user.user.color, 0.1) ??
                              "rgb(0 0 0 / 10%)"),
                        }}
                      >
                        {user?.user?.positionInSector[0]
                          ? user?.user?.positionInSector[0]?.name
                          : "-"}
                      </div>
                    </div>
                    <div className="mobile-user-meta">
                      <span className="badge">
                        {view === "week" ? t("Week") : t("Month")}
                      </span>
                      <span className="meta-hours">
                        {userTotal ? `${userTotal.toFixed(2)}h` : "0h"}
                      </span>
                    </div>
                  </div>
                }
              >
                <div className="mobile-dates">
                  {visibleDates.map((dateStr) => {
                    const date = new Date(dateStr);
                    const shiftsForDate = user.shifts.filter((shift: any) =>
                      isSameDay(new Date(shift.date), date),
                    );

                    return (
                      <div
                        className="mobile-date-block"
                        key={dateStr.toString()}
                      >
                        <div className="mobile-date-header">
                          <span className="day">{t(format(date, "EEE"))}</span>
                          <span className="num">{format(date, "d")}</span>
                          <span className="month">{format(date, "MMM")}</span>
                        </div>

                        <div className="mobile-shifts-list">
                          {shiftsForDate.length === 0 && (
                            <div className="empty-cell">-</div>
                          )}
                          {shiftsForDate.map((shift: any, i: number) => {
                            const shiftSingle: { shiftType: ShiftType } = {
                              shiftType: shift.shiftType,
                            };
                            return (
                              <div
                                key={i}
                                className="mobile-shift-chip"
                                style={{
                                  borderColor: user?.user?.color ?? "#999",
                                  background: colorToRgba(
                                    user?.user?.color ?? "#999",
                                    0.1,
                                  ),
                                }}
                              >
                                <div className="chip-main">
                                  {shift.shiftType === "HOURS" ? (
                                    <>
                                      <span className="time">
                                        {formatTime(shift.startTime)} -{" "}
                                        {formatTime(shift.endTime)}
                                      </span>
                                      <span className="duration">
                                        {formatDuration(shift.durationInHours)}h
                                      </span>
                                    </>
                                  ) : shift.shiftType ===
                                    ShiftTypePanorama.DAILY_WAGE ? (
                                    <span className="badge">D</span>
                                  ) : shift.shiftType ===
                                    ShiftTypePanorama.TEMPORARY_HELP ? (
                                    <span className="badge">I</span>
                                  ) : (
                                    <span className="badge">OD</span>
                                  )}
                                  {getShiftLocationName(shift) && (
                                    <span style={{ fontSize: 11, opacity: 0.75, marginLeft: 4 }}>
                                      {getShiftLocationName(shift)}
                                    </span>
                                  )}
                                  <span className="abbr">
                                    {ShiftAbbreviation[shiftSingle.shiftType]}
                                  </span>
                                </div>
                                <div className="chip-actions">
                                  {canDeleteSingle && (
                                    <Button
                                      type="link"
                                      size="small"
                                      onClick={() => deleteShift(shift)}
                                    >
                                      {t("Delete")}
                                    </Button>
                                  )}
                                  {canDeleteMultiple && shift?.unitId && (
                                    <Button
                                      type="link"
                                      size="small"
                                      onClick={() =>
                                        deleteMultipleShifts(shift)
                                      }
                                    >
                                      {t("Delete multiple shifts")}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {canCreateAny && (
                          <button
                            className="mobile-add-btn"
                            onClick={() => {
                              setShiftModalOpen({
                                user: user.user,
                                date: dateStr,
                                shift: shiftsForDate,
                              });
                            }}
                          >
                            <PlusOutlined />
                            {t("Add shift")}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Collapse.Panel>
            );
          })}
        </Collapse>
      )}
      {shiftModalOpen && (
        <AddShiftModal
          isOpen={shiftModalOpen}
          onCloseModal={() => setShiftModalOpen(undefined)}
          onAddShift={(value: any) => {
            addShift(value);
          }}
          shiftModalOpen={shiftModalOpen}
        />
      )}
    </div>
  );
};

export default ShiftScheduler;
